import i18next, { type i18n as I18nInstance } from "i18next";
import { initReactI18next } from "react-i18next";
import { setLibraryMessageProvider } from "echart-table-library";
import en from "./locales/en.json";
import fr from "./locales/fr.json";
import ja from "./locales/ja.json";
import type { AppLocaleTag } from "./index";

/**
 * i18next instance (latest `i18next` + `react-i18next`):
 *
 * - Translations live in the JSON files under `shared/i18n/locales/`
 *   (`en.json` / `ja.json` / `fr.json`) — edit those to change ANY string,
 *   including the library's (`lib.*` section), no library/app code touched.
 * - The instance is passed to the library through
 *   `setLibraryMessageProvider`: the library's `tLibrary` looks up `lib.*`
 *   keys here first and falls back to its built-in dictionary, so the app's
 *   JSON is the single source of truth.
 * - Later extension without a rebuild: add `i18next-http-backend` (or any
 *   custom loader) below and fetch the same JSON shape from a URL — the
 *   consuming code (`useTranslation`, the provider) does not change.
 * - `escapeValue: false` — React escapes interpolated text already; the
 *   library re-interpolates `{param}` placeholders itself after lookup.
 */

const RESOURCES = {
  en: { translation: en },
  ja: { translation: ja },
  fr: { translation: fr },
} as const;

/**
 * The DEFAULT i18next instance: `react-i18next`'s `useTranslation` binds to
 * it without needing an `<I18nextProvider>` wrapper around the router.
 */
export const i18n: I18nInstance = i18next;

/** The library reads `lib.*` keys from this instance (see module docs). */
function registerLibraryMessageProvider(): void {
  setLibraryMessageProvider((key, locale) => {
    const lookupKey = `lib.${key}`;
    if (!i18n.hasLoadedNamespace("translation") || !i18n.exists(lookupKey, { lng: locale })) {
      return undefined;
    }
    return i18n.t(lookupKey, { lng: locale });
  });
}

/** Initialize i18next once, before the React tree renders (idempotent). */
export function initI18n(initialLanguage: AppLocaleTag): void {
  if (i18n.isInitialized) {
    return;
  }
  void i18n.use(initReactI18next).init({
    resources: RESOURCES,
    lng: initialLanguage,
    fallbackLng: "en",
    supportedLngs: ["en", "ja", "fr"],
    load: "languageOnly",
    defaultNS: "translation",
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
  });
  registerLibraryMessageProvider();
}
